import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import { useDispatch } from "react-redux";
import {
  requestAddRiskAssets,
  requestAddRiskWeaknesses,
} from "../../redux/actions/risksActionCreators";
import { translate } from "../../utils/utils";
import { TextField, useMediaQuery } from "@mui/material";
import { Search } from "tabler-icons-react";
import { InputAdornment } from "@mui/material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F3FBF9",
    color: "#B1B4BC",
    height: "50px",
    padding: "0px 16px 0px 16px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {},
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function AddDetails({
  open,
  handleClose,
  assets,
  risk,
  type,
  weaknesses,
  accountId,
  accountType,
  handleSearchAsset,
  handleSearchWeakness,
  handleWeaknessPage,
  handleAssetPage,
  pageAsset,
  totalAssetPages,
  totalWeaknessesPages,
  pageWeakness,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [updatedAssets, setUpdatedAssets] = useState([]);
  const [selectedWeaknesses, setSelectedWeaknesses] = useState([]);
  const [updatedWeaknesses, setUpdatedWeaknesses] = useState([]);
  const [search, setSearch] = useState("");
  const [newAssets, setNewAssets] = useState([]);
  const [newWeaknesses, setNewWeaknesses] = useState([]);

  useEffect(() => {
    let uniqueIds = {};
    let filtredList = assets.filter((obj) => {
      if (!uniqueIds[obj.id]) {
        uniqueIds[obj.id] = true;
        return true;
      }
      return false;
    });

    setNewAssets(filtredList);
  }, [assets]);

  useEffect(() => {
    let uniqueIds = {};
    let filtredList = weaknesses.filter((obj) => {
      if (!uniqueIds[obj.id]) {
        uniqueIds[obj.id] = true;
        return true;
      }
      return false;
    });

    setNewWeaknesses(filtredList);
  }, [weaknesses]);

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (type === "asset") {
      if (
        scrollHeight - scrollTop === clientHeight &&
        totalAssetPages > pageAsset
      ) {
        handleAssetPage(pageAsset + 1);
      }
    } else {
      if (
        scrollHeight - scrollTop === clientHeight &&
        totalWeaknessesPages > pageWeakness
      ) {
        handleWeaknessPage(pageWeakness + 1);
      }
    }
  };

  const handleCheckAsset = (id) => {
    if (!selectedAssets.includes(id)) {
      setSelectedAssets([...selectedAssets, id]);
    } else {
      setSelectedAssets(selectedAssets.filter((el) => el !== id));
    }
  };

  const handleCheckWeakness = (id) => {
    if (!selectedWeaknesses.includes(id)) {
      setSelectedWeaknesses([...selectedWeaknesses, id]);
    } else {
      setSelectedWeaknesses(selectedWeaknesses.filter((el) => el !== id));
    }
  };

  useEffect(() => {
    if (risk?.assets) {
      setUpdatedAssets(risk?.assets?.concat(selectedAssets));
    } else {
      setUpdatedAssets(selectedAssets);
    }
  }, [selectedAssets]);

  useEffect(() => {
    if (risk?.weaknesses) {
      setUpdatedWeaknesses(risk?.weaknesses?.concat(selectedWeaknesses));
    } else {
      setUpdatedWeaknesses(selectedWeaknesses);
    }
  }, [selectedWeaknesses]);

  const handleDetails = () => {
    if (type == "asset") {
      dispatch(
        requestAddRiskAssets(
          risk?.id,
          risk?.[translate("title")],
          risk?.[translate("description")],
          updatedAssets,
          risk?.weaknesses,
          accountId,
          accountType
        )
      );
      handleClose();
      setSelectedAssets([]);
      setSearch("");
    } else {
      dispatch(
        requestAddRiskWeaknesses(
          risk?.id,
          risk?.[translate("title")],
          risk?.[translate("description")],
          risk?.assets,
          updatedWeaknesses,
          accountId,
          accountType
        )
      );
      handleClose();
      setSelectedWeaknesses([]);
      setSearch("");
    }
  };

  useEffect(() => {
    if (type === "asset") {
      handleSearchAsset(search);
    } else {
      handleSearchWeakness(search);
    }
  }, [search]);

  const isMobile = useMediaQuery("(max-width:1400px)");
  return (
    <div>
      <Dialog
        open={open}
        onClose={() => {
          handleClose();
          setSearch("");
        }}
        maxWidth="xl"
        PaperProps={{
          sx: !isMobile
            ? { width: "35%", padding: "20px " }
            : { width: "90%", height: "80%", margin: "5px 0", padding: "5px " },
        }}
      >
        <Typography
          variant="subtitle3"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          <IconButton
            onClick={() => {
              handleClose();
              setSearch("");
            }}
          >
            <KeyboardBackspaceIcon sx={{ width: "70%", color: "black" }} />
          </IconButton>
          {t("back")}
        </Typography>
        <DialogTitle>
          <Typography variant="title4">
            {type === "asset" ? t("add_asset") : t("add_weakness")}
          </Typography>
        </DialogTitle>
        <div style={{ padding: "0 24px" }}>
          <TextField
            fullWidth
            id="outlined-start-adornment"
            placeholder={t("search")}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {" "}
                  <Search size={22} strokeWidth={2} color="#8F9BB2" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <DialogContent>
          <TableContainer
            onScroll={handleScroll}
            component={Paper}
            className="scrollhost"
            sx={{
              width: "100%",
              height: !isMobile ? "550px" : "100%",
              overflowY: !isMobile ? "scroll" : "scroll",
              "&.MuiTableContainer-root": {
                borderRadius: "0px",
                boxShadow: "none",
              },
              mt: !isMobile ? 3 : 0,
            }}
          >
            <Table
              stickyHeader
              aria-label="customized table"
              sx={{
                width: "100%",
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "#DDF6F0",
                  cursor: "pointer",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left" width="70%">
                    {t("name")}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="30%">
                    {t("source")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              {type === "asset" ? (
                <TableBody>
                  {newAssets?.map((el) => (
                    <StyledTableRow
                      key={el.id}
                      onClick={() => handleCheckAsset(el.id)}
                      sx={{
                        backgroundColor:
                          selectedAssets.includes(el.id) && "#F3FBF9",
                      }}
                    >
                      <StyledTableCell align="left">
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            checked={selectedAssets.includes(el.id)}
                            color="success"
                          />
                          <span>
                            {" "}
                            {el?.[translate("title")] || t("not_specified")}
                          </span>
                        </div>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {el.source}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  {newWeaknesses?.map((el) => (
                    <StyledTableRow
                      key={el.id}
                      onClick={() => handleCheckWeakness(el.id)}
                      sx={{
                        backgroundColor:
                          selectedWeaknesses.includes(el.id) && "#F3FBF9",
                      }}
                    >
                      <StyledTableCell align="left">
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            checked={selectedWeaknesses.includes(el.id)}
                            color="success"
                          />
                          <span>
                            {" "}
                            {el?.[translate("title")] || t("not_specified")}
                          </span>
                        </div>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {el.source}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mt: 4 }}>
          <Button
            variant="contained"
            color="button"
            sx={{ width: "94%" }}
            onClick={handleDetails}
          >
            {t("add")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
